import { z } from 'zod';
import { FC, useEffect } from 'react';
import { useApiMutation, useApiQuery } from '../../../query/hooks';
import { graphql } from '../../../gql';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	Tooltip,
} from '@mui/material';
import { FormField } from '../../../form/FormField';
import { FormErrors } from '../../../form/FormErrors';
import { useModalContext } from '../../../modal/ModalContext';
import { ControlledFormField } from '../../../form/ControlledFormField';
import { MetricBehaviorSelect } from '../MetricBehaviorSelect';
import { useTypedForm } from '../../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { getBehaviorHintText, getTrendComparisonHintText } from '../utils/getHintTexts';
import { useSnackbar } from '../../../store/snackbar';

const editMetricTemplateModalSchema = z.object({
	id: z.string().optional(),
});

const getMetricTemplateQuery = graphql(`
	query settings_getMetricTemplate($id: UUID!) {
		getMetricTemplate(by: { id: $id }) {
			id
			name
			isSystem
			behavior
			precision
			unit
			unitSign
			expectations
			defaultTrendComparison
		}
	}
`);

const createMetricTemplateMutation = graphql(`
	mutation settings_createMetricTemplate($data: MetricTemplateCreateInput!) {
		createMetricTemplate(data: $data) {
			ok
			errorMessage
		}
	}
`);

const updateMetricTemplateMutation = graphql(`
	mutation settings_updateMetricTemplate($id: UUID!, $data: MetricTemplateUpdateInput!) {
		updateMetricTemplate(by: { id: $id }, data: $data) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.metric-template.error.empty-name' })),
		behavior: z.enum(['latest', 'average', 'sum', 'proportion']),
		precision: z.preprocess(
			(numeric) => parseInt(z.string().parse(numeric), 10),
			z
				.number()
				.min(0, intl.formatMessage({ id: 'form.metric-template.error.invalid-precision' }))
				.max(10, intl.formatMessage({ id: 'form.metric-template.error.invalid-precision' })),
		),
		unit: z.enum(['number', 'currency', 'percentage']),
		unitSign: z.string().optional(),
		expectations: z.enum(['increase', 'decrease']),
		defaultTrendComparison: z.enum(['absolute', 'relative']),
	});

type Props = z.infer<typeof editMetricTemplateModalSchema>;

export type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

const EditMetricTemplateModal: FC<Props> = ({ id }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		values: {
			name: '',
			behavior: 'latest',
			precision: '0',
			unit: 'number',
			unitSign: undefined,
			expectations: 'increase',
			defaultTrendComparison: 'absolute',
		},
		schema: createFormSchema(intl),
	});
	const { handleSubmit, formState, reset } = form;

	const { data: queryData, isLoading: isMetricTemplateLoading } = useApiQuery(getMetricTemplateQuery, {
		enabled: id != null,
		variables: {
			id: id ?? '',
		},
	});
	// sync form initial values
	useEffect(() => {
		reset({
			name: queryData?.getMetricTemplate?.name,
			behavior: queryData?.getMetricTemplate?.behavior,
			precision: queryData?.getMetricTemplate?.precision.toString(),
			unit: queryData?.getMetricTemplate?.unit ?? 'number',
			unitSign: queryData?.getMetricTemplate?.unitSign ?? undefined,
			// radios need to have default value because they are controlled inputs
			expectations: queryData?.getMetricTemplate?.expectations ?? 'increase',
			defaultTrendComparison: queryData?.getMetricTemplate?.defaultTrendComparison ?? 'absolute',
		});
	}, [queryData, reset]);

	const { mutate: createMetricTemplate } = useApiMutation(createMetricTemplateMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createMetricTemplate.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.createMetricTemplate.errorMessage,
				callback: closeModal,
				openSnackbar,
			});
		},
	});

	const { mutate: updateMetricTemplate } = useApiMutation(updateMetricTemplateMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateMetricTemplate.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateMetricTemplate.errorMessage,
				callback: closeModal,
				openSnackbar,
			});
		},
	});

	const onSubmit = (values: FormValues) => {
		if (id == null) {
			createMetricTemplate({
				data: values,
			});
		} else {
			updateMetricTemplate({
				id,
				data: values,
			});
		}
	};

	return (
		// @ts-ignore
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>
				{id == null ? (
					<FormattedMessage id={'form.metric-template.create.title'} />
				) : (
					<FormattedMessage id={'form.metric-template.update.title'} />
				)}
			</DialogTitle>
			<Divider />
			<DialogContent>
				{isMetricTemplateLoading ? (
					<Stack p={3} alignItems={'center'}>
						<CircularProgress />
					</Stack>
				) : (
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'form.metric-template.label.name' })}
							render={(props) => (
								<Tooltip
									title={
										queryData?.getMetricTemplate?.isSystem === true
											? intl.formatMessage({ id: 'form.metric-template.system-name-edit-disabled' })
											: undefined
									}
								>
									<OutlinedInput disabled={queryData?.getMetricTemplate?.isSystem === true} {...props} />
								</Tooltip>
							)}
						/>
						<ControlledFormField
							form={form}
							name={'behavior'}
							label={intl.formatMessage({ id: 'form.metric-template.label.behavior' })}
							description={intl.formatMessage({ id: 'form.metric-template.label.behavior.description' })}
							render={(props) => <MetricBehaviorSelect {...props} />}
							hint={getBehaviorHintText(intl, form.watch('behavior'))}
						/>
						<FormField
							form={form}
							name={'precision'}
							label={intl.formatMessage({ id: 'form.metric-template.label.precision' })}
							description={intl.formatMessage({ id: 'form.metric-template.label.precision.description' })}
							render={(props) => <OutlinedInput type={'number'} sx={{ width: 100 }} inputProps={{ min: 0, max: 10 }} {...props} />}
						/>
						<ControlledFormField
							form={form}
							name={'unit'}
							label={intl.formatMessage({ id: 'form.metric-template.label.unit' })}
							description={intl.formatMessage({ id: 'form.metric-template.label.unit.description' })}
							render={(props) => (
								<Select {...props}>
									<MenuItem value={'number'}>
										<FormattedMessage id={'form.metric-template.label.number'} />
									</MenuItem>
									<MenuItem value={'currency'}>
										<FormattedMessage id={'form.metric-template.label.currency'} />
									</MenuItem>
									<MenuItem value={'percentage'}>
										<FormattedMessage id={'form.metric-template.label.percentage'} />
									</MenuItem>
								</Select>
							)}
						/>
						<FormField
							form={form}
							name={'unitSign'}
							label={intl.formatMessage({ id: 'form.metric-template.label.unit-sign' })}
							render={(props) => <OutlinedInput type={'text'} sx={{ width: 100 }} inputProps={{ min: 0, max: 10 }} {...props} />}
						/>
						<ControlledFormField
							form={form}
							name={'expectations'}
							label={intl.formatMessage({ id: 'form.metric-template.label.expectations' })}
							description={intl.formatMessage({ id: 'form.metric-template.label.expectations.description' })}
							render={(props) => (
								<Select {...props}>
									<MenuItem value={'increase'}>
										<FormattedMessage id={'form.metric-template.label.increase'} />
									</MenuItem>
									<MenuItem value={'decrease'}>
										<FormattedMessage id={'form.metric-template.label.decrease'} />
									</MenuItem>
								</Select>
							)}
						/>
						<ControlledFormField
							form={form}
							name={'defaultTrendComparison'}
							label={intl.formatMessage({ id: 'form.metric-template.label.trend-comparison' })}
							description={intl.formatMessage({ id: 'form.metric-template.label.trend-comparison.description' })}
							render={(props) => (
								<Select {...props}>
									<MenuItem value={'absolute'}>
										<FormattedMessage id={'form.metric-template.label.absolute'} />
									</MenuItem>
									<MenuItem value={'relative'}>
										<FormattedMessage id={'form.metric-template.label.relative'} />
									</MenuItem>
								</Select>
							)}
							hint={getTrendComparisonHintText(intl, form.watch('defaultTrendComparison'))}
						/>
						<FormErrors form={form} />
					</Grid>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting || isMetricTemplateLoading}>
						{id == null ? <FormattedMessage id={'common.create'} /> : <FormattedMessage id={'common.save'} />}
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { editMetricTemplateModalSchema, EditMetricTemplateModal };
